import { Info } from '@mui/icons-material';
import { IconButton, Popover, Typography, useTheme } from '@mui/material';

import { AXLE_TEAL } from '../../constants/colors';
import { useStateWithDerivedBoolean } from '../../utils/booleanState';
import { ClickEvent } from '../../utils/clickEvent';
import { Children, ReturnComponent } from '../../utils/reactTypes';
import { If } from '../utils/conditionalComponents';
import { HorizontalStack, HorizontalStackProps } from '../utils/linearLayouts';

export const InfoIconAnchor = ({
    children,
    disabledHelperText,
    description,
    enabled = true,
    icon = undefined,
    ...rest
}: {
    enabled?: boolean;
    children: Children;
    disabledHelperText?: string;
    description?: Children;
    icon?: Children;
} & Omit<HorizontalStackProps, 'children'>): ReturnComponent => {
    const theme = useTheme();

    // Popover functionality that shows either the disabled helper text or the description.
    const showDisabledHelperTextInsteadOfDescription = !enabled && disabledHelperText !== '';
    const [popoverAnchor, setPopoverAnchor, isPopoverOpen, closePopover] =
        useStateWithDerivedBoolean<HTMLElement | null>(null);
    const popoverContents = showDisabledHelperTextInsteadOfDescription
        ? disabledHelperText
        : description;

    return (
        <HorizontalStack {...rest}>
            {children}
            <If condition={!!description || showDisabledHelperTextInsteadOfDescription}>
                <IconButton
                    onMouseEnter={(event: ClickEvent) => setPopoverAnchor(event.currentTarget)}
                    onMouseLeave={() => setPopoverAnchor(null)}
                    aria-label='More information'
                >
                    {icon ?? <Info style={{ fontSize: 15, color: AXLE_TEAL }} />}
                </IconButton>
            </If>
            <Popover
                sx={{
                    pointerEvents: 'none',
                }}
                open={isPopoverOpen}
                anchorEl={popoverAnchor}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={closePopover}
                disableRestoreFocus
            >
                <Typography variant='body2' sx={{ padding: theme.spacing(2), maxWidth: '300px' }}>
                    {popoverContents}
                </Typography>
            </Popover>
        </HorizontalStack>
    );
};
